(() => {
  const elementName = 'dd-video-gallery';

  /*
   * El archivo puede cargarse varias veces porque cada sección
   * lo referencia. Esta comprobación evita registrar dos veces
   * el mismo Custom Element.
   */
  if (customElements.get(elementName)) return;

  class DDVideoGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.viewport = this.querySelector('.ddvg__viewport');
      this.track = this.querySelector('.ddvg__track');

      this.slides = Array.from(
        this.querySelectorAll('.ddvg__slide')
      );

      this.videos = Array.from(
        this.querySelectorAll('.ddvg__video')
      );

      this.previousButton = this.querySelector(
        '.ddvg__arrow--previous'
      );

      this.nextButton = this.querySelector(
        '.ddvg__arrow--next'
      );

      this.mode = this.dataset.mode || 'carousel_interactive';
      this.isCarousel = this.mode !== 'single';

      this.autoplaySlider =
        this.dataset.autoplaySlider === 'true';

      this.autoplayDelay = Math.max(
        1000,
        Number(this.dataset.autoplayDelay) || 5000
      );

      this.pauseOnHover =
        this.dataset.pauseOnHover === 'true';

      this.loopCarousel =
        this.dataset.loopCarousel === 'true';

      this.perViewDesktop = Math.max(
        1,
        Number(this.dataset.perViewDesktop) || 3
      );

      this.perViewMobile = Math.max(
        1,
        Number(this.dataset.perViewMobile) || 1
      );

      this.isHovered = false;
      this.isPointerDown = false;

      this.autoplayTimer = null;
      this.scrollEndTimer = null;
      this.resizeTimer = null;

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.handleDocumentVisibility =
        this.handleDocumentVisibility.bind(this);

      document.addEventListener(
        'visibilitychange',
        this.handleDocumentVisibility,
        { signal: this.signal }
      );

      this.setupVideos();

      if (this.isCarousel) {
        this.setupCarousel();
      }
    }

    disconnectedCallback() {
      this.stopCarouselAutoplay();

      window.clearTimeout(this.scrollEndTimer);
      window.clearTimeout(this.resizeTimer);

      if (this.videoObserver) {
        this.videoObserver.disconnect();
        this.videoObserver = null;
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.initialized = false;
    }

    /* =====================================================
       VIDEOS
    ===================================================== */

    setupVideos() {
      this.videos.forEach((video) => {
        const slide = video.closest('.ddvg__slide');

        if (!slide) return;

        /*
         * La propiedad muted también se establece mediante JS.
         * Algunos navegadores requieren tanto el atributo HTML
         * como la propiedad JavaScript.
         */
        video.muted = true;
        video.defaultMuted = true;

        const behavior = slide.dataset.behavior;

        if (behavior === 'interactive') {
          const playButton = slide.querySelector(
            '.ddvg__play-button'
          );

          video.addEventListener(
            'click',
            () => {
              this.toggleInteractiveVideo(video, slide);
            },
            { signal: this.signal }
          );

          video.addEventListener(
            'keydown',
            (event) => {
              if (
                event.key === 'Enter' ||
                event.key === ' '
              ) {
                event.preventDefault();
                this.toggleInteractiveVideo(video, slide);
              }
            },
            { signal: this.signal }
          );

          if (playButton) {
            playButton.addEventListener(
              'click',
              (event) => {
                event.preventDefault();
                event.stopPropagation();

                this.toggleInteractiveVideo(video, slide);
              },
              { signal: this.signal }
            );
          }

          video.addEventListener(
            'ended',
            () => {
              this.handleInteractiveVideoEnded(video, slide);
            },
            { signal: this.signal }
          );

          this.updateInteractiveButton(slide);
        }
      });

      this.setupVideoObserver();
    }

    setupVideoObserver() {
      if (!('IntersectionObserver' in window)) {
        this.videos.forEach((video) => {
          if (video.dataset.autoMuted === 'true') {
            video.muted = true;
            this.safePlay(video);
          }
        });

        return;
      }

      /*
       * En carrusel, el área visible del propio carrusel es
       * utilizada como referencia. De este modo, los videos
       * que salen horizontalmente se pausan.
       */
      const observerRoot = this.isCarousel
        ? this.viewport
        : null;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            const slide = video.closest('.ddvg__slide');

            if (!slide) return;

            const isVisible =
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.2;

            video.dataset.visible = String(isVisible);

            if (isVisible) {
              this.autoplayMutedVideoWhenNeeded(
                video,
                slide
              );
            } else {
              this.deactivateVideo(video, slide);
            }
          });
        },
        {
          root: observerRoot,
          threshold: [0, 0.1, 0.2, 0.5, 0.75, 1]
        }
      );

      this.videos.forEach((video) => {
        this.videoObserver.observe(video);
      });
    }

    autoplayMutedVideoWhenNeeded(video, slide) {
      if (document.hidden) return;

      const shouldAutoplayMuted =
        video.dataset.autoMuted === 'true';

      const userPaused =
        video.dataset.userPaused === 'true';

      const isListening =
        slide.classList.contains('is-listening');

      if (
        shouldAutoplayMuted &&
        !userPaused &&
        !isListening
      ) {
        video.muted = true;
        this.safePlay(video);
      }
    }

    deactivateVideo(video, slide) {
      video.pause();
      video.muted = true;

      delete video.dataset.userPaused;

      slide.classList.remove('is-listening');

      this.updateInteractiveButton(slide);
      this.startCarouselAutoplay();
    }

    toggleInteractiveVideo(video, slide) {
      const isListening =
        slide.classList.contains('is-listening') &&
        !video.muted &&
        !video.paused;

      /*
       * Si ya se está escuchando, el siguiente clic lo pausa.
       */
      if (isListening) {
        video.pause();
        video.muted = true;

        video.dataset.userPaused = 'true';

        slide.classList.remove('is-listening');

        this.updateInteractiveButton(slide);
        this.startCarouselAutoplay();

        return;
      }

      /*
       * Solo puede existir un video con sonido dentro de
       * cada instancia de la sección.
       */
      this.videos.forEach((otherVideo) => {
        if (otherVideo === video) return;

        const otherSlide = otherVideo.closest(
          '.ddvg__slide'
        );

        if (
          !otherSlide ||
          !otherSlide.classList.contains('is-listening')
        ) {
          return;
        }

        otherVideo.muted = true;
        otherSlide.classList.remove('is-listening');

        this.updateInteractiveButton(otherSlide);

        /*
         * Si el otro video pertenece a un carrusel automático
         * y continúa visible, vuelve a reproducirse en silencio.
         */
        if (
          otherVideo.dataset.autoMuted === 'true' &&
          otherVideo.dataset.visible === 'true'
        ) {
          this.safePlay(otherVideo);
        } else {
          otherVideo.pause();
        }
      });

delete video.dataset.userPaused;

/*
 * Cuando el usuario decide escuchar el video,
 * se reinicia desde el principio.
 */
video.pause();

try {
  video.currentTime = 0;
} catch (error) {
  console.log('No se pudo reiniciar el video:', error);
}

video.muted = false;
slide.classList.add('is-listening');

this.updateInteractiveButton(slide);
this.stopCarouselAutoplay();

const playPromise = video.play();

      if (
        playPromise &&
        typeof playPromise.catch === 'function'
      ) {
        playPromise.catch(() => {
          /*
           * Si el navegador no permite sonido, se mantiene
           * la reproducción automática silenciada.
           */
          video.muted = true;
          slide.classList.remove('is-listening');

          this.updateInteractiveButton(slide);

          if (video.dataset.autoMuted === 'true') {
            this.safePlay(video);
          }

          this.startCarouselAutoplay();
        });
      }
    }

    handleInteractiveVideoEnded(video, slide) {
      video.muted = true;
      video.currentTime = 0;

      video.dataset.userPaused = 'true';

      slide.classList.remove('is-listening');

      this.updateInteractiveButton(slide);
      this.startCarouselAutoplay();
    }

    updateInteractiveButton(slide) {
      const button = slide.querySelector(
        '.ddvg__play-button'
      );

      if (!button) return;

      const listening =
        slide.classList.contains('is-listening');

      button.setAttribute(
        'aria-label',
        listening
          ? 'Pausar video'
          : 'Reproducir con sonido'
      );
    }

    safePlay(video) {
      const playPromise = video.play();

      if (
        playPromise &&
        typeof playPromise.catch === 'function'
      ) {
        playPromise.catch(() => {
          video.muted = true;
        });
      }
    }

    handleDocumentVisibility() {
      if (document.hidden) {
        this.videos.forEach((video) => {
          const slide = video.closest('.ddvg__slide');

          video.pause();
          video.muted = true;

          if (slide) {
            slide.classList.remove('is-listening');
            this.updateInteractiveButton(slide);
          }
        });

        this.stopCarouselAutoplay();
        return;
      }

      this.videos.forEach((video) => {
        const slide = video.closest('.ddvg__slide');

        if (
          slide &&
          video.dataset.visible === 'true'
        ) {
          this.autoplayMutedVideoWhenNeeded(
            video,
            slide
          );
        }
      });

      this.startCarouselAutoplay();
    }

    /* =====================================================
       CARRUSEL
    ===================================================== */

    setupCarousel() {
      if (!this.viewport || !this.slides.length) return;

      if (this.previousButton) {
        this.previousButton.addEventListener(
          'click',
          () => {
            this.moveCarousel(-1, true);
          },
          { signal: this.signal }
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          () => {
            this.moveCarousel(1, true);
          },
          { signal: this.signal }
        );
      }

      this.viewport.addEventListener(
        'scroll',
        () => {
          this.handleCarouselScroll();
        },
        {
          passive: true,
          signal: this.signal
        }
      );

      this.viewport.addEventListener(
        'pointerdown',
        () => {
          this.isPointerDown = true;
          this.stopCarouselAutoplay();
        },
        {
          passive: true,
          signal: this.signal
        }
      );

      window.addEventListener(
        'pointerup',
        () => {
          this.isPointerDown = false;
          this.startCarouselAutoplay();
        },
        {
          passive: true,
          signal: this.signal
        }
      );

      window.addEventListener(
        'resize',
        () => {
          window.clearTimeout(this.resizeTimer);

          this.resizeTimer = window.setTimeout(
            () => {
              const maxIndex = this.getMaximumIndex();
              const currentIndex = this.getCurrentIndex();

              if (currentIndex > maxIndex) {
                this.scrollToIndex(maxIndex, false);
              }

              this.updateArrowStates();
              this.startCarouselAutoplay();
            },
            150
          );
        },
        {
          passive: true,
          signal: this.signal
        }
      );

      if (this.pauseOnHover) {
        this.addEventListener(
          'mouseenter',
          () => {
            this.isHovered = true;
            this.stopCarouselAutoplay();
          },
          { signal: this.signal }
        );

        this.addEventListener(
          'mouseleave',
          () => {
            this.isHovered = false;
            this.startCarouselAutoplay();
          },
          { signal: this.signal }
        );
      }

      requestAnimationFrame(() => {
        this.updateArrowStates();
        this.startCarouselAutoplay();
      });
    }

    handleCarouselScroll() {
      this.stopCarouselAutoplay();

      window.clearTimeout(this.scrollEndTimer);

      this.scrollEndTimer = window.setTimeout(
        () => {
          this.updateArrowStates();

          if (!this.isPointerDown) {
            this.startCarouselAutoplay();
          }
        },
        180
      );
    }

    getVideosPerView() {
      return window.matchMedia(
        '(max-width: 749px)'
      ).matches
        ? this.perViewMobile
        : this.perViewDesktop;
    }

    getMaximumIndex() {
      const videosPerView = this.getVideosPerView();

      return Math.max(
        0,
        this.slides.length - videosPerView
      );
    }

    getCurrentIndex() {
      if (!this.viewport || !this.slides.length) {
        return 0;
      }

      const currentScroll = this.viewport.scrollLeft;

      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(
          slide.offsetLeft - currentScroll
        );

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return Math.min(
        nearestIndex,
        this.getMaximumIndex()
      );
    }

    moveCarousel(direction, userTriggered = false) {
      const maximumIndex = this.getMaximumIndex();

      if (maximumIndex <= 0) return;

      const currentIndex = this.getCurrentIndex();

      let targetIndex = currentIndex + direction;

      if (targetIndex > maximumIndex) {
        targetIndex = this.loopCarousel
          ? 0
          : maximumIndex;
      }

      if (targetIndex < 0) {
        targetIndex = this.loopCarousel
          ? maximumIndex
          : 0;
      }

      this.scrollToIndex(targetIndex, true);

      if (userTriggered) {
        this.stopCarouselAutoplay();
        this.startCarouselAutoplay();
      }
    }

    scrollToIndex(index, smooth = true) {
      if (!this.viewport || !this.slides.length) return;

      const maximumIndex = this.getMaximumIndex();

      const safeIndex = Math.max(
        0,
        Math.min(index, maximumIndex)
      );

      const targetSlide = this.slides[safeIndex];

      if (!targetSlide) return;

      this.viewport.scrollTo({
        left: targetSlide.offsetLeft,
        behavior:
          smooth && !this.reducedMotion
            ? 'smooth'
            : 'auto'
      });

      this.updateArrowStates(safeIndex);
    }

    updateArrowStates(forcedIndex = null) {
      const maximumIndex = this.getMaximumIndex();

      const currentIndex =
        forcedIndex === null
          ? this.getCurrentIndex()
          : forcedIndex;

      const noMovementAvailable =
        maximumIndex <= 0;

      if (this.previousButton) {
        this.previousButton.hidden =
          noMovementAvailable;

        this.previousButton.disabled =
          !this.loopCarousel &&
          currentIndex <= 0;
      }

      if (this.nextButton) {
        this.nextButton.hidden =
          noMovementAvailable;

        this.nextButton.disabled =
          !this.loopCarousel &&
          currentIndex >= maximumIndex;
      }
    }

    hasListeningVideo() {
      return Boolean(
        this.querySelector(
          '.ddvg__slide.is-listening'
        )
      );
    }

    startCarouselAutoplay() {
      this.stopCarouselAutoplay();

      if (!this.isCarousel) return;
      if (!this.autoplaySlider) return;
      if (document.hidden) return;
      if (this.isHovered) return;
      if (this.isPointerDown) return;
      if (this.hasListeningVideo()) return;

      const maximumIndex = this.getMaximumIndex();

      if (maximumIndex <= 0) return;

      const currentIndex = this.getCurrentIndex();

      /*
       * Si el carrusel no es circular y llegó al final,
       * se detiene.
       */
      if (
        !this.loopCarousel &&
        currentIndex >= maximumIndex
      ) {
        return;
      }

      this.autoplayTimer = window.setTimeout(
        () => {
          this.moveCarousel(1, false);
        },
        this.autoplayDelay
      );
    }

    stopCarouselAutoplay() {
      if (this.autoplayTimer) {
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define(
    elementName,
    DDVideoGallery
  );
})();